import React, { useState, useCallback, useMemo } from 'react';
import { 
  Activity, Clock, CheckCircle, XCircle, Play, Pause, 
  BarChart3, Users, Zap, Timer, Filter, RefreshCw
} from 'lucide-react';
import { SessionProgress, AdvancedFilters, DetailedAuditorStats, FilterPreset, ViewFilterPresets } from '../types/AdminTypes';

// ✅ Interface corrigée - Ajout des taskDetails
interface SessionsViewProps {
  sessions: SessionProgress[];
  taskDetails?: any; // ✅ AJOUT - Les vraies données des tâches
  filters: AdvancedFilters;
  setFilters: (filters: AdvancedFilters) => void;
  auditorStats: DetailedAuditorStats[];
  filterPresets: ViewFilterPresets;
  activePreset: string;
  onApplyPreset: (preset: FilterPreset) => void;
  onApplyCustomDates: (dateFrom: string, dateTo: string) => void;
}

const SessionsView: React.FC<SessionsViewProps> = ({ 
  sessions, 
  taskDetails, // ✅ AJOUT
  filters, 
  setFilters, 
  auditorStats,
  filterPresets,
  activePreset,
  onApplyPreset,
  onApplyCustomDates
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // ✅ STATISTIQUES CORRIGÉES - Utiliser taskDetails pour les vraies données
  const stats = useMemo(() => {
    // Stats des sessions (pour les compteurs de sessions)
    const activeSessions = sessions.filter(s => s.session_status === 'in_progress').length;
    const completedSessions = sessions.filter(s => s.session_status === 'completed').length;
    
    // ✅ CORRECTION : Stats des tâches (pour les vrais totaux)
    let totalTasks = 0;
    let completedTasks = 0;
    let approvedTasks = 0;
    let rejectedTasks = 0;

    if (taskDetails && taskDetails.length > 0) {
      // Utiliser les vraies données des tâches
      totalTasks = taskDetails.length;
      completedTasks = taskDetails.filter((t: any) => 
        t.audit_status === 'approved' || t.audit_status === 'rejected'
      ).length;
      approvedTasks = taskDetails.filter((t: any) => t.audit_status === 'approved').length;
      rejectedTasks = taskDetails.filter((t: any) => t.audit_status === 'rejected').length;
    } else {
      // Fallback sur les sessions si pas de taskDetails
      totalTasks = sessions.reduce((sum, s) => sum + s.total_tasks, 0);
      completedTasks = sessions.reduce((sum, s) => sum + s.completed_tasks, 0);
      approvedTasks = sessions.reduce((sum, s) => sum + (s.validated_tasks || 0), 0);
      rejectedTasks = sessions.reduce((sum, s) => sum + (s.rejected_tasks || 0), 0);
    }

    const globalProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    console.log('📊 Stats calculées:', {
      source: taskDetails ? 'taskDetails' : 'sessions',
      totalTasks,
      completedTasks,
      approvedTasks,
      rejectedTasks,
      globalProgress
    });

    return {
      activeSessions,
      completedSessions,
      totalTasks,
      completedTasks,
      approvedTasks,
      rejectedTasks,
      globalProgress
    };
  }, [sessions, taskDetails]);

  // ✅ DÉTECTER SI DES FILTRES SONT APPLIQUÉS
  const hasFiltersApplied = useMemo(() => 
    !!(filters.sessionStatus || filters.auditorId || filters.dateFrom || filters.dateTo), 
    [filters]
  );

  // ... resto du code identique jusqu'aux métriques ...

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            🔴 Suivi Temps Réel
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            {hasFiltersApplied ? (
              <>
                <span className="font-medium text-blue-600">Vue filtrée</span> • 
                {sessions.length} session(s) • {stats.totalTasks} tâches • 
                Actualisation automatique
              </>
            ) : (
              <>Activité en cours • {stats.totalTasks} tâches total • Actualisation automatique toutes les 30s</>
            )}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2 ${
              hasFiltersApplied
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filtres</span>
            {hasFiltersApplied && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                !
              </span>
            )}
          </button>
          <div className="flex items-center space-x-2 text-sm text-green-600">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Live</span>
          </div>
        </div>
      </div>

      {/* Filtres - identique */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📅 Périodes rapides
            </label>
            <div className="flex flex-wrap gap-2">
              {filterPresets.presets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => onApplyPreset(preset)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activePreset === preset.label
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <select
              value={filters.sessionStatus}
              onChange={(e) => setFilters({ ...filters, sessionStatus: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="in_progress">🔴 En cours</option>
              <option value="completed">✅ Terminées</option>
              <option value="paused">⏸️ En pause</option>
              <option value="abandoned">❌ Abandonnées</option>
            </select>

            <select
              value={filters.auditorId}
              onChange={(e) => setFilters({ ...filters, auditorId: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les auditeurs</option>
              {auditorStats
                .filter(auditor => auditor && auditor.auditor && auditor.auditor.full_name)
                .map(auditorStat => (
                  <option key={auditorStat.auditor_id} value={auditorStat.auditor_id}>
                    {auditorStat.auditor.full_name}
                  </option>
                ))}
            </select>

            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Date de début"
            />

            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Date de fin"
            />
          </div>
        </div>
      )}

      {/* ✅ MÉTRIQUES CORRIGÉES - Utiliser les vraies stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Sessions Actives</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.activeSessions}</p>
            </div>
            <Activity className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Sessions Terminées</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.completedSessions}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">
                {/* ✅ CORRECTION : Afficher le vrai total des tâches */}
                Tâches Total
              </p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.totalTasks}</p>
              <p className="text-purple-200 text-xs">
                {stats.approvedTasks} validées • {stats.rejectedTasks} commentées
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Progression Globale</p>
              <p className="text-2xl sm:text-3xl font-bold">{stats.globalProgress}%</p>
              <p className="text-orange-200 text-xs">
                {stats.completedTasks}/{stats.totalTasks} traitées
              </p>
            </div>
            <Zap className="w-8 h-8 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Resto du composant identique... */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            Sessions en Temps Réel
            {hasFiltersApplied && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({sessions.length} session(s) filtrée(s))
              </span>
            )}
          </h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <RefreshCw className="w-4 h-4" />
            <span>Mise à jour automatique</span>
          </div>
        </div>

        {/* Liste des sessions - identique à avant */}
        {sessions.length > 0 ? (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div key={session.session_id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                {/* Contenu identique à avant */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {session.auditor.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{session.auditor.full_name}</p>
                      <p className="text-sm text-gray-500">{session.auditor.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className="px-3 py-1 rounded-full text-xs font-medium border bg-yellow-100 text-yellow-800 border-yellow-200">
                      ⏸️ Paused
                    </span>
                  </div>
                </div>

                {/* Stats de la session (pas changées) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">{session.total_tasks}</p>
                    <p className="text-xs text-gray-500">Session tâches</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{session.completed_tasks}</p>
                    <p className="text-xs text-gray-500">Session complétées</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{session.validated_tasks || 0}</p>
                    <p className="text-xs text-gray-500">Validées</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{session.rejected_tasks || 0}</p>
                    <p className="text-xs text-gray-500">Commentées</p>
                  </div>
                </div>

                {/* Barre de progression */}
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Progression Session</span>
                    <span>{session.progress_percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${session.progress_percentage}%` }}
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs text-gray-500">
                  <span>Créée le {new Date(session.created_at).toLocaleString('fr-FR')}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Aucune session trouvée</h3>
            <p className="text-gray-600">
              {hasFiltersApplied 
                ? "Aucune session ne correspond aux filtres sélectionnés"
                : "Aucune session d'audit active pour le moment"
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsView;